import {
  frontOffices,
  frontOffice,
  frontOfficeUpdate,
  frontOfficeCreate,
  frontOfficeFeatureList,
  frontOfficeSEOCharCounter,
} from '@/services/frontoffice.service'
import { publishing, getFrontOfficeSettings, updateFrontOfficeSettings } from '@/services/algolia.service'
import { tableOptions, buildCoreApiParams } from '@/utils/table.util'

const SET_AVAILABLE_FRONT_OFFICES = 'SET_AVAILABLE_FRONT_OFFICES'
const SET_FRONT_OFFICES = 'SET_FRONT_OFFICES'
const SET_FRONT_OFFICES_MOBILE = 'SET_FRONT_OFFICES_MOBILE'
const SET_FRONT_OFFICES_PAGINATION = 'SET_FRONT_OFFICES_PAGINATION'
const SET_CURRENT_FRONT_OFFICE = 'SET_CURRENT_FRONT_OFFICE'
const SET_CURRENT_FRONT_OFFICE_SETTINGS = 'SET_CURRENT_FRONT_OFFICE_SETTINGS'
const SET_CURRENT_FRONT_OFFICE_FEATURE_LIST = 'SET_CURRENT_FRONT_OFFICE_FEATURE_LIST'
const SET_CURRENT_FRONT_OFFICE_SEO_CHAR_COUNTER = 'SET_CURRENT_FRONT_OFFICE_SEO_CHAR_COUNTER'
const RESET_STATE = 'RESET_STATE'

const getDefaultState = () => {
  return {
    availableFrontOffices: [],
    frontOffices: [],
    frontOfficesPagination: {},
    frontOfficesMobile: [],
    currentFrontOffice: {},
    currentFrontOfficeSettings: {},
    currentFrontOfficeFeatureList: [],
    currentFrontOfficeSeoCharCount: {},
  }
}

const state = getDefaultState()

const mutations = {
  [SET_AVAILABLE_FRONT_OFFICES]: (state, availableFrontOffices) => {
    state.availableFrontOffices = availableFrontOffices
  },
  [SET_FRONT_OFFICES]: (state, frontOffices) => {
    state.frontOffices = frontOffices
  },
  [SET_FRONT_OFFICES_MOBILE]: (state, { frontOffices, reset }) => {
    if (reset) {
      state.frontOfficesMobile = []
    }
    state.frontOfficesMobile.push(...frontOffices)
  },
  [SET_FRONT_OFFICES_PAGINATION]: (state, pagination) => {
    state.frontOfficesPagination = pagination
  },
  [SET_CURRENT_FRONT_OFFICE]: (state, frontOffice) => {
    state.currentFrontOffice = frontOffice
    localStorage.setItem('frontoffice', JSON.stringify(frontOffice))
  },
  [SET_CURRENT_FRONT_OFFICE_SETTINGS]: (state, frontOfficeSettings) => {
    state.currentFrontOfficeSettings = frontOfficeSettings
  },
  [SET_CURRENT_FRONT_OFFICE_FEATURE_LIST]: (state, frontOfficeFeatureList) => {
    state.currentFrontOfficeFeatureList = frontOfficeFeatureList
  },
  [SET_CURRENT_FRONT_OFFICE_SEO_CHAR_COUNTER]: (state, currentFrontOfficeSeoCharCount) => {
    state.currentFrontOfficeSeoCharCount = currentFrontOfficeSeoCharCount
  },
  [RESET_STATE]: state => {
    Object.assign(state, getDefaultState())
  },
}

const actions = {
  getAvailableFrontOffices: ({ commit, rootState }, { urlParams = '', locationId = null }) => {
    return frontOffices(rootState.client.currentClient.id, urlParams, locationId)
      .then(response => {
        commit(SET_AVAILABLE_FRONT_OFFICES, response.results)
        return Promise.resolve()
      })
      .catch(e => Promise.reject(e))
  },
  getFrontOffices: ({ commit, rootState }, { tableParams = null, resetFrontOffices, search, locationId = '' }) => {
    const urlParams = tableOptions(tableParams, search, 'frontOffices')
    return frontOffices(rootState.client.currentClient.id, urlParams, locationId)
      .then(response => {
        commit(SET_FRONT_OFFICES, response.results)

        commit(SET_FRONT_OFFICES_MOBILE, { frontOffices: response.results, reset: resetFrontOffices })
        delete response.results
        commit(SET_FRONT_OFFICES_PAGINATION, response)
        return Promise.resolve()
      })
      .catch(e => Promise.reject(e))
  },
  getFrontOffice: ({ commit, rootState }, { frontOfficeId, localeId = '', locationId = '' }) => {
    const urlParams = buildCoreApiParams({
      params: { frontOfficeId },
      query: [
        { key: 'clientId', value: rootState.client.currentClient.id },
        { key: 'localeId', value: localeId },
        { key: 'locationId', value: locationId },
      ],
    })

    return frontOffice(urlParams).then(frontOffice => {
      commit(SET_CURRENT_FRONT_OFFICE, frontOffice)
      return Promise.resolve()
    })
  },
  updateFrontOffice: ({ commit, state, rootState }, data) => {
    return frontOfficeUpdate(
      rootState.client.currentClient.id,
      state.currentFrontOffice.id,
      state.currentFrontOffice.localeId,
      state.currentFrontOffice.defaultLocaleId === state.currentFrontOffice.localeId,
      data
    ).then(response => {
      commit(SET_CURRENT_FRONT_OFFICE, response.data)
      return Promise.resolve()
    })
  },
  updateCurrentFrontOffice: ({ dispatch }, frontOffice) => {
    return dispatch('getFrontOffice', { frontOfficeId: frontOffice.id, localeId: frontOffice.defaultLocaleId })
  },
  updateCurrentFrontOfficeLocale: ({ state, dispatch }, locale) => {
    return dispatch('getFrontOffice', { frontOfficeId: state.currentFrontOffice.id, localeId: locale.localeId })
  },
  createFrontOffice: (_, data) => {
    return frontOfficeCreate(data.clientId, data).then(response => {
      return Promise.resolve(response.data)
    })
  },
  publishFrontOffice: ({ state, rootState }) => {
    return publishing(rootState.client.currentClient.id, state.currentFrontOffice.id)
      .then(() => Promise.resolve())
      .catch(() => Promise.reject())
  },
  getFrontOfficeSettings: async ({ commit, state, rootState }) => {
    try {
      const { data } = await getFrontOfficeSettings(rootState.client.currentClient.id, state.currentFrontOffice.id)
      commit(SET_CURRENT_FRONT_OFFICE_SETTINGS, data.results)
    } catch {
      return false
    }
  },
  updateFrontOfficeSettings: async ({ commit, state, rootState }, newData) => {
    try {
      const { data } = await updateFrontOfficeSettings(
        rootState.client.currentClient.id,
        state.currentFrontOffice.id,
        newData
      )
      commit(SET_CURRENT_FRONT_OFFICE_SETTINGS, data.results)
    } catch {
      return false
    }
  },
  getFrontOfficeFeaturesList: async ({ commit, state }) => {
    try {
      let detailList = []
      const { data } = await frontOfficeFeatureList(state.currentFrontOffice.clientId, state.currentFrontOffice.id)
      data.customListsGlobal
        .filter(itemCustomLists => itemCustomLists.locale === state.currentFrontOffice.localeCode)
        .forEach(itemListDefault => (detailList = itemListDefault.lists))

      commit(SET_CURRENT_FRONT_OFFICE_FEATURE_LIST, detailList)
    } catch {
      commit(SET_CURRENT_FRONT_OFFICE_FEATURE_LIST, [])
    }
  },

  getFrontOfficeSeoCharCount: async ({ commit, state }) => {
    try {
      const { data } = await frontOfficeSEOCharCounter(
        state.currentFrontOffice.clientId,
        state.currentFrontOffice.id,
        state.currentFrontOffice.localeId
      )
      commit(SET_CURRENT_FRONT_OFFICE_SEO_CHAR_COUNTER, data)
    } catch {
      commit(SET_CURRENT_FRONT_OFFICE_SEO_CHAR_COUNTER, null)
    }
  },
  resetState({ commit }) {
    commit(RESET_STATE)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
}
